import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import styles from './style.module.scss';
import classNames from 'classnames';
import Heading from 'shared/components/common/heading/Heading';
import CustomInput from 'shared/components/common/custom_input/CustomInput';
import CustomButton from 'shared/components/common/custom_button/CustomButton';
import { useSelector } from 'react-redux';
import { CreateMatchProductMutation, UpdateMatchProductMutation } from 'shared/services/match_product_service/MatchProductService';
import OverlayLoading from 'shared/screen_loader/overlay_loader/OverlayLoading';
import { MatchProductVS } from 'shared/utils/validation/AuthValidation';
import { useFormik } from 'formik';
import { toastMessage } from 'shared/components/toast/Toast';
import CustomTextarea from 'shared/components/common/custom_text_area/CustomTextArea';
import UploadAvatarArray from 'shared/components/common/upload_avatar/UploadAvatarArray';
import { uploadFileMedia } from 'shared/services/media_service/Media_Service';

export default function AddMatchProduct({ show, handleClose, isEdit = false, selectedItem, datatable }) {
    const { mutate: createMutate, isLoading: createLoading, isSuccess: createSuccess } = CreateMatchProductMutation();
    const { mutate: updateMutate, isLoading: updateLoading, isSuccess: updateSuccess } = UpdateMatchProductMutation();
    const {
        theme: { theme }
    } = useSelector((state) => state.root);

    const [avatarFileArray, setAvatarFileArray] = useState([]);
    const [newFiles, setNewFiles] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (show && isEdit && selectedItem) {
            setFieldValue('product_name', selectedItem.product_name || '');
            setFieldValue('group', selectedItem.group || '');
            setFieldValue('height', selectedItem.height || '');
            setFieldValue('width', selectedItem.width || '');
            if (selectedItem.images && selectedItem.images.length > 0) {
                setAvatarFileArray(selectedItem.images);
            } else if (selectedItem.image) {
                // Backward compatibility
                setAvatarFileArray([selectedItem.image]);
            } else {
                setAvatarFileArray([]);
            }
        } else if (show && !isEdit) {
            formik.resetForm();
            setAvatarFileArray([]);
            setNewFiles([]);
        }
    }, [show, isEdit, selectedItem]);

    useEffect(() => {
        if ((createSuccess || updateSuccess) && show) {
            onClose();
        }
    }, [createSuccess, updateSuccess]);

    const onClose = () => {
        handleClose();
        formik.resetForm();
        setAvatarFileArray([]);
        setNewFiles([]);
        setIsSubmitting(false);
    };

    const initialValues = {
        product_name: '',
        group: '',
        height: '',
        width: ''
    };

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: initialValues,
        validationSchema: MatchProductVS,
        onSubmit: (value) => {
            submitHandler(value);
        }
    });

    const { handleChange, setFieldValue, handleSubmit, values, touched, errors } = formik;

    const uploadMedia = async () => {
        try {
            let urls = [...avatarFileArray];
            for (let file of newFiles) {
                if (typeof file !== 'string') {
                    let fileBody = new FormData();
                    fileBody.append('image', file);
                    const resp = await uploadFileMedia(fileBody);
                    if (resp?.data) {
                        urls.push(resp.data.url);
                    }
                }
            }
            return urls;
        } catch (error) {
            console.error("Image upload failed", error);
            return [];
        }
    };

    const submitHandler = async (formValues) => {
        const isDuplicate = datatable?.some(
            (item) =>
                item.product_name.trim().toLowerCase() === formValues.product_name.trim().toLowerCase() &&
                (!isEdit || item._id !== selectedItem?._id)
        );

        if (isDuplicate) {
            toastMessage('error', `A match for Product "${formValues.product_name}" already exists!`);
            return;
        }

        setIsSubmitting(true);
        try {
            let imageUrls = await uploadMedia();

            const payload = {
                product_name: formValues.product_name,
                group: formValues.group,
                height: formValues.height,
                width: formValues.width,
                images: imageUrls
            };

            if (isEdit && selectedItem) {
                updateMutate({
                    id: selectedItem._id,
                    payload
                }, {
                    onSettled: () => {
                        setIsSubmitting(false);
                    }
                });
            } else {
                createMutate(payload, {
                    onSettled: () => {
                        setIsSubmitting(false);
                    }
                });
            }
        } catch (error) {
            console.error(error);
            setIsSubmitting(false);
        }
    };

    return (
        <Modal show={show} onHide={onClose} size="md" aria-labelledby="contained-modal-title-vcenter" centered>
            <Modal.Header className={styles.Header}>
                <Heading heading={isEdit ? 'Edit Match Product' : 'Add Match Product'} headingStyle={styles.AddLinkHeading} />
            </Modal.Header>
            <Modal.Body className={classNames(`${theme}ModalBackground`, styles.Body)}>
                <div className="d-flex justify-content-center mb-3">
                    <UploadAvatarArray 
                        avatarFileArray={avatarFileArray} 
                        setAvatarFileArray={setAvatarFileArray} 
                        setArray={() => {}}
                        setFile={(files) => {
                            if (Array.isArray(files)) {
                                setNewFiles(prev => [...prev, ...files]);
                                const previews = files.map(file => URL.createObjectURL(file));
                                setAvatarFileArray(prev => [...prev, ...previews]);
                            }
                        }} 
                    />
                </div>
                <div className={classNames(styles.AddLinkContainer)}>
                    <CustomTextarea
                        Label={'Product Name (Model)'}
                        type={'text'}
                        Placeholder={'e.g. iPhone 15 Pro Max, iPhone 14 Pro'}
                        CustomInputStyle={classNames(`${theme}ItemBackground`, styles.CustomInputStyle)}
                        CustomLabelStyle={styles.CustomLabelStyle}
                        InputContainerStyle={styles.InputContainerStyle}
                        Value={values.product_name}
                        OnChange={handleChange('product_name')}
                        error={errors.product_name && touched.product_name ? errors.product_name : ''}
                    />
                    <CustomInput
                        label={'Group'}
                        placeholder={'e.g. Group 1'}
                        CustomInputStyle={classNames(`${theme}ItemBackground`, styles.CustomInputStyle)}
                        CustomLabelStyle={styles.CustomLabelStyle}
                        InputContainerStyle={styles.InputContainerStyle}
                        value={values.group}
                        onChange={handleChange('group')}
                        error={errors.group && touched.group ? errors.group : ''}
                    />
                    <div className="d-flex gap-2 w-100">
                        <div className="w-50">
                            <CustomInput
                                label={'Height'}
                                placeholder={'e.g. 150.80'}
                                CustomInputStyle={classNames(`${theme}ItemBackground`, styles.CustomInputStyle)}
                                CustomLabelStyle={styles.CustomLabelStyle}
                                InputContainerStyle={styles.InputContainerStyle}
                                value={values.height}
                                onChange={handleChange('height')}
                                error={errors.height && touched.height ? errors.height : ''}
                            />
                        </div>
                        <div className="w-50">
                            <CustomInput
                                label={'Width'}
                                placeholder={'e.g. 71.70'}
                                CustomInputStyle={classNames(`${theme}ItemBackground`, styles.CustomInputStyle)}
                                CustomLabelStyle={styles.CustomLabelStyle}
                                InputContainerStyle={styles.InputContainerStyle}
                                value={values.width}
                                onChange={handleChange('width')}
                                error={errors.width && touched.width ? errors.width : ''}
                            />
                        </div>
                    </div>

                    <div className="d-flex justify-content-end mt-4">
                        <CustomButton text={'Cancel'} containerStyle={styles.CancelBtn} submitHandle={onClose} />
                        <CustomButton text={isEdit ? 'Update' : 'Add'} containerStyle={styles.SubmitBtn} submitHandle={handleSubmit} />
                    </div>
                </div>
                {(isSubmitting || createLoading || updateLoading) && <OverlayLoading />}
            </Modal.Body>
        </Modal>
    );
}
